import React from 'react';
import { parseLocalDate, formatDateOnly } from '../utils/dateUtils';

const ClientCharts = ({ meetings }) => {
  // Agrupar datos por mes
  const monthlyData = meetings.reduce((acc, meeting) => {
    const date = parseLocalDate(meeting.date);
    const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`; // YYYY-M

    if (!acc[monthYear]) {
      acc[monthYear] = {
        contactsGenerated: 0,
        patients: 0,
        meetingsCount: 0,
        monthLabel: date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })
      };
    }

    acc[monthYear].contactsGenerated += meeting.contactsGenerated;
    acc[monthYear].patients += meeting.patients;
    acc[monthYear].meetingsCount += 1;

    return acc;
  }, {});

  // Convertir a array y ordenar por fecha
  const sortedMonthlyData = Object.keys(monthlyData)
    .sort((a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    })
    .map(key => {
      const data = monthlyData[key];
      const conversionRate = data.contactsGenerated > 0 ? (data.patients / data.contactsGenerated) * 100 : 0;
      return {
        month: data.monthLabel,
        contacts: data.contactsGenerated,
        patients: data.patients,
        conversion: parseFloat(conversionRate.toFixed(1))
      };
    });

  // Función para dibujar una gráfica de barras agrupadas
  const renderGroupedBarChart = (data) => {
    if (data.length < 1) {
      return <p className="text-[#63666A] text-sm">No hay datos suficientes para mostrar la gráfica de rendimiento.</p>;
    }

    const maxContacts = Math.max(...data.map(d => d.contacts));
    const maxPatients = Math.max(...data.map(d => d.patients));
    const maxConversion = Math.max(...data.map(d => d.conversion));

    // Determinar el valor máximo para escalar las barras
    const overallMaxBarValue = Math.max(maxContacts, maxPatients, 100);
    const chartHeight = 180; // Altura fija del área de gráfico

    return (
      <div className="w-full bg-white rounded-lg shadow-sm p-4">
        <h4 className="font-medium text-[#0061A8] mb-4">Rendimiento Mensual</h4>
        
        {/* Leyenda */}
        <div className="flex justify-end space-x-4 text-sm mb-4">
          <div className="flex items-center">
            <span className="w-3 h-3 bg-blue-500 rounded-full mr-1"></span> Contactos
          </div>
          <div className="flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-1"></span> Pacientes
          </div>
          <div className="flex items-center">
            <span className="w-3 h-3 bg-purple-500 rounded-full mr-1"></span> Conversión (%)
          </div>
        </div>

        {/* Área del gráfico */}
        <div className="relative border-b border-l border-[#D1D3D4]" style={{ height: `${chartHeight}px` }}>
          {/* Líneas de referencia horizontales */}
          <div className="absolute inset-0">
            {[0, 25, 50, 75, 100].map(percent => (
              <div
                key={percent}
                className="absolute w-full border-t border-gray-200"
                style={{ bottom: `${(percent / 100) * chartHeight}px` }}
              >
                <span className="absolute -left-8 -top-2 text-xs text-gray-500">
                  {Math.round((percent / 100) * overallMaxBarValue)}
                </span>
              </div>
            ))}
          </div>

          {/* Barras */}
          <div className="absolute bottom-0 left-0 w-full h-full flex items-end justify-around">
            {data.map((d, i) => {
              const contactsHeight = overallMaxBarValue > 0 ? (d.contacts / overallMaxBarValue) * chartHeight : 0;
              const patientsHeight = overallMaxBarValue > 0 ? (d.patients / overallMaxBarValue) * chartHeight : 0;
              const conversionHeight = overallMaxBarValue > 0 ? (d.conversion / overallMaxBarValue) * chartHeight : 0;

              return (
                <div key={i} className="flex flex-col items-center">
                  {/* Grupo de barras */}
                  <div className="flex items-end space-x-1">
                    {/* Barra de Contactos */}
                    <div className="relative group">
                      <div
                        className="bg-blue-500 rounded-t-sm transition-all duration-300 hover:bg-blue-600"
                        style={{ 
                          height: `${contactsHeight}px`, 
                          width: '16px',
                          minHeight: d.contacts > 0 ? '2px' : '0px'
                        }}
                      ></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {d.contacts} contactos
                      </div>
                    </div>
                    
                    {/* Barra de Pacientes */}
                    <div className="relative group">
                      <div
                        className="bg-green-500 rounded-t-sm transition-all duration-300 hover:bg-green-600"
                        style={{ 
                          height: `${patientsHeight}px`, 
                          width: '16px',
                          minHeight: d.patients > 0 ? '2px' : '0px'
                        }}
                      ></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {d.patients} pacientes
                      </div>
                    </div>
                    
                    {/* Barra de Conversión */}
                    <div className="relative group">
                      <div
                        className="bg-purple-500 rounded-t-sm transition-all duration-300 hover:bg-purple-600"
                        style={{ 
                          height: `${conversionHeight}px`, 
                          width: '16px',
                          minHeight: d.conversion > 0 ? '2px' : '0px'
                        }}
                      ></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {d.conversion}% conversión
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Etiquetas debajo del gráfico */}
        <div className="flex justify-around mt-2">
          {data.map((d, i) => (
            <div key={i} className="flex flex-col items-center">
              {/* Valores numéricos con mejor espaciado */}
              <div className="text-xs font-semibold mb-1">
                <div className="flex justify-center items-center space-x-2">
                  <span className="text-blue-700">{d.contacts}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-green-700">{d.patients}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-purple-700">{d.conversion}%</span>
                </div>
              </div>
              
              {/* Etiqueta del mes */}
              <span className="text-xs text-[#63666A] text-center font-medium">{d.month}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const generateInsights = (data, allMeetings) => {
    let insights = [];

    // Interpretación de la gráfica de barras verticales
    insights.push(`\n*Interpretación de la Gráfica de Rendimiento Mensual (Barras Verticales):*`);
    insights.push(`  - Cada grupo de tres barras representa un mes. La primera barra (azul) muestra los Contactos generados, la segunda (verde) los Pacientes concretados, y la tercera (morada) la Tasa de Conversión (en porcentaje).`);
    insights.push(`  - La altura de cada barra indica la magnitud de la métrica para ese mes. Puedes comparar visualmente el desempeño de cada métrica a lo largo del tiempo.`);
    insights.push(`  - Observa cómo se relacionan las barras: un aumento en Contactos sin un aumento proporcional en Pacientes podría indicar una baja eficiencia en la conversión, mientras que un aumento en Pacientes con Contactos estables podría significar una mejora en la calidad de los contactos o en el proceso de seguimiento.`);
    insights.push(`  - La barra morada de Conversión te da una idea directa de la efectividad de tus esfuerzos para transformar contactos en pacientes en cada periodo.`);


    // Comparación Junta Actual vs Junta Anterior
    if (allMeetings.length >= 2) {
      const lastMeeting = allMeetings[allMeetings.length - 1];
      const prevMeeting = allMeetings[allMeetings.length - 2];

      insights.push(`\n*Comparación Junta Actual (${formatDateOnly(lastMeeting.date)}) vs Junta Anterior (${formatDateOnly(prevMeeting.date)}):*`);
      
      // Contactos
      if (lastMeeting.contactsGenerated > prevMeeting.contactsGenerated) {
        insights.push(`  - Contactos generados: Aumento de ${prevMeeting.contactsGenerated} a ${lastMeeting.contactsGenerated} (+${lastMeeting.contactsGenerated - prevMeeting.contactsGenerated}).`);
      } else if (lastMeeting.contactsGenerated < prevMeeting.contactsGenerated) {
        insights.push(`  - Contactos generados: Disminución de ${prevMeeting.contactsGenerated} a ${lastMeeting.contactsGenerated} (${lastMeeting.contactsGenerated - prevMeeting.contactsGenerated}).`);
      } else {
        insights.push(`  - Contactos generados: Se mantuvieron estables en ${lastMeeting.contactsGenerated}.`);
      }

      // Pacientes
      if (lastMeeting.patients > prevMeeting.patients) {
        insights.push(`  - Pacientes concretados: Aumento de ${prevMeeting.patients} a ${lastMeeting.patients} (+${lastMeeting.patients - prevMeeting.patients}).`);
      } else if (lastMeeting.patients < prevMeeting.patients) {
        insights.push(`  - Pacientes concretados: Disminución de ${prevMeeting.patients} a ${lastMeeting.patients} (${lastMeeting.patients - prevMeeting.patients}).`);
      } else {
        insights.push(`  - Pacientes concretados: Se mantuvieron estables en ${lastMeeting.patients}.`);
      }

      // Tasa de Conversión
      const lastConversion = lastMeeting.contactsGenerated > 0 ? (lastMeeting.patients / lastMeeting.contactsGenerated) * 100 : 0;
      const prevConversion = prevMeeting.contactsGenerated > 0 ? (prevMeeting.patients / prevMeeting.contactsGenerated) * 100 : 0;
      
      if (lastConversion > prevConversion) {
        insights.push(`  - Tasa de Conversión: Mejoró del ${prevConversion.toFixed(1)}% al ${lastConversion.toFixed(1)}%.`);
      } else if (lastConversion < prevConversion) {
        insights.push(`  - Tasa de Conversión: Disminuyó del ${prevConversion.toFixed(1)}% al ${lastConversion.toFixed(1)}%.`);
      } else {
        insights.push(`  - Tasa de Conversión: Se mantuvo en ${lastConversion.toFixed(1)}%.`);
      }
    } else {
      insights.push("No hay suficientes juntas para realizar una comparación entre la junta actual y la anterior.");
    }

    // Interpretación de la tasa de conversión mensual
    if (data.length > 0) {
      const avgConversion = data.reduce((sum, d) => sum + d.conversion, 0) / data.length;
      insights.push(`\n*Análisis Mensual de Tasa de Conversión:*`);
      insights.push(`  - La tasa de conversión promedio mensual es del ${avgConversion.toFixed(1)}%.`);
      if (data.length >= 2) {
        const lastMonthData = data[data.length - 1];
        const prevMonthData = data[data.length - 2];
        if (lastMonthData.conversion > prevMonthData.conversion) {
          insights.push(`  - Se observa una mejora en la conversión del último mes (${lastMonthData.conversion}%) respecto al anterior (${prevMonthData.conversion}%).`);
        } else if (lastMonthData.conversion < prevMonthData.conversion) {
          insights.push(`  - La conversión del último mes (${lastMonthData.conversion}%) ha disminuido respecto al anterior (${prevMonthData.conversion}%).`);
        }
      }
    }

    // Recomendaciones
    insights.push(`\n*Recomendaciones y Posibles Acciones:*`);
    if (data.length > 0) {
      const lastMonthData = data[data.length - 1];
      if (lastMonthData.conversion < 50) {
        insights.push(`  - Evaluar el proceso de calificación de contactos o el seguimiento para mejorar la conversión de ${lastMonthData.conversion}%.`);
      } else if (lastMonthData.conversion >= 70) {
        insights.push(`  - Mantener las estrategias actuales de conversión y buscar escalar la generación de contactos.`);
      }
      if (lastMonthData.contacts === 0) {
        insights.push(`  - Urgente: Implementar acciones para generar nuevos contactos este mes.`);
      }
    } else {
      insights.push(`  - Registrar más juntas para obtener datos y generar recomendaciones más precisas.`);
    }

    return insights.join('\n');
  };

  const insightsReport = generateInsights(sortedMonthlyData, meetings);

  return (
    <div className="mt-6 space-y-8">
      <h3 className="text-lg font-semibold text-[#0061A8]">Análisis de Rendimiento Mensual</h3>
      
      {renderGroupedBarChart(sortedMonthlyData)}

      <div className="bg-white rounded-lg shadow-sm p-4">
        <h4 className="font-medium text-[#0061A8] mb-4">Reporte de Insights Clave</h4>
        <pre className="whitespace-pre-wrap font-sans text-sm text-[#63666A]">{insightsReport}</pre>
      </div>
    </div>
  );
};

export default ClientCharts;
